import { Logger } from '~~/helpers/logger';
import { userGetters, useUser } from '~/utils/ct';
import { NEWSLETTER_SUBSCRIPTION } from '~~/constants/localStorage';
import { SECONDS_IN_HOUR } from '~~/constants/timeUnits';
import { dateToYYYYMMDD } from '~~/helpers/time/dateToYYYYMMDD/dateToYYYYMMDD';
import { unixToMilliseconds } from '~~/helpers/time/unixToMilliseconds';
import { formatNumberAsCurrency } from '~~/helpers/formatNumberAsCurrency';
import type { NewsletterSubscriptionData } from '~~/types/product/NewsletterSubscriptionData';
import type { Customer } from '~~/types/commercetools';
import { NEWSLETTER_VOUCHER_CODE } from '~~/constants/discounts';
import { NEWSLETTER_SUBSCRIPTION_WIDGET } from '~~/constants/newsletterSubscriptionWidget';

export const useNewsletterVoucher = () => {

  const { languageAndCountry, countryCode, currencyCode } = useLocalization();
  const { event: ciaEvent } = useCia();
  const i18n = useI18n();

  const localStorageKey = computed(() => `${NEWSLETTER_SUBSCRIPTION}_${languageAndCountry.value}`);

  const voucherSettings = computed(() => NEWSLETTER_VOUCHER_CODE[countryCode.value]);
  const widgetSettings = computed(() => NEWSLETTER_SUBSCRIPTION_WIDGET[countryCode.value]);

  const voucherValue = computed(() =>
    formatNumberAsCurrency(voucherSettings.value.voucherValue, {
      i18n,
      currencyCode: currencyCode.value,
    }),
  );
  const minimumPurchaseValue = computed(() =>
    formatNumberAsCurrency(voucherSettings.value.minimumPurchaseValue, {
      i18n,
      currencyCode: currencyCode.value,
    }),
  );

  function getLocalSubscriptionProgress(): NewsletterSubscriptionData {

    try {

      const savedData = localStorage.getItem(localStorageKey.value);

      if (savedData) {

        const parsedData = JSON.parse(savedData);

        return parsedData;

      }

    } catch (error) {

      Logger.error('Failed to parse newsletter subscription progress data from localStorage:', error);

    }

    return {
      showFromDate: 0,
      status: 'initiated',
    };

  }

  function saveLocalSubscriptionProgress(subscriptionProgress: NewsletterSubscriptionData) {

    try {

      localStorage.setItem(localStorageKey.value, JSON.stringify(subscriptionProgress));

    } catch (error) {

      Logger.error('Failed to save newsletter subscription progress data to localStorage:', error);

    }

  }

  function postponeWidgetDisplay() {

    if (import.meta.server) {

      return;

    }

    const currentTimeInSeconds = Math.floor(Date.now() / 1000);
    const notVisibleUntil = currentTimeInSeconds + SECONDS_IN_HOUR;
    const subscriptionProgressData = getLocalSubscriptionProgress();

    if (subscriptionProgressData?.showFromDate == null) {

      return;

    }

    subscriptionProgressData.showFromDate = notVisibleUntil;
    saveLocalSubscriptionProgress(subscriptionProgressData);

  }

  async function canRenderSubscriptionWidget(): Promise<boolean> {

    if (import.meta.server) {

      return false;

    }

    const voucherHasValue = voucherSettings.value?.voucherValue > 0;
    const widgetEnabled = widgetSettings.value?.widgetEnabled;

    if (!voucherHasValue || !widgetEnabled) {

      return false;

    }

    const currentTimeInSeconds = Math.floor(Date.now() / 1000);
    const bloomreachProgressData = await fetchBloomreachSubscriptionProgress();
    const localStorageProgressData = getLocalSubscriptionProgress();

    // show voucher when local storage data is missing
    if (!localStorageProgressData) {

      return true;

    }

    const showFromDateReached = localStorageProgressData.showFromDate <= currentTimeInSeconds;

    // if Bloomreach data is missing, rely on local storage only
    if (!bloomreachProgressData) {

      return showFromDateReached;

    }

    const unusedVouchersList = bloomreachProgressData.unused_vouchers || [];
    const hasNewsletterConsent = bloomreachProgressData.newsletter_consent || false;
    const voucherCode = bloomreachProgressData.newsletter_subscription_progress?.voucher_code || null;
    const hasVoucherCode = !!voucherCode;
    const voucherUsed = !!(voucherCode && !unusedVouchersList.includes(voucherCode));
    const voucherEndDate = bloomreachProgressData.newsletter_subscription_progress?.voucher_end_date || null;
    const voucherExpired = !!(voucherEndDate && currentTimeInSeconds > voucherEndDate);
    const hasValidVoucher = hasVoucherCode && !voucherUsed && !voucherExpired;
    const currentSubscriptionStatus = localStorageProgressData?.status || '';

    if (hasValidVoucher) {

      localStorageProgressData.status = 'voucher';
      saveLocalSubscriptionProgress(localStorageProgressData);

    }

    // Do not load subscription widget:
    // until show_from_date is reached
    if (!showFromDateReached) {

      return false;

    }

    // Do not load subscription widget:
    // when customer has consent and voucher is not valid
    if (hasNewsletterConsent && !hasValidVoucher) {

      return false;

    }

    // Do not load subscription widget:
    // when customer has consent and the process of subscribing where never started
    // do not offer the voucher to customers who have the consent
    if (hasNewsletterConsent && (currentSubscriptionStatus === 'initial' || currentSubscriptionStatus === '')) {

      return false;

    }

    return true;

  }

  async function fetchBloomreachSubscriptionProgress() {

    if (import.meta.server) {

      return null;

    }

    const { user, load } = useUser();

    if (!user.value) {

      await load();

    }

    const emailAddress = computed(() => userGetters.getEmailAddress(user.value as Customer));

    let subscriptionProgressData = getLocalSubscriptionProgress();
    const email = emailAddress.value ? emailAddress.value : subscriptionProgressData?.email;

    if (!email) {

      return null;

    }

    try {

      const res = await ciaEvent.newsletterSubscriptionProgress(email);

      if (res?.newsletter_subscription_progress != null) {

        const progress = res.newsletter_subscription_progress;
        const voucherExpiryDate = dateToYYYYMMDD(new Date(unixToMilliseconds(progress.voucher_end_date)));

        subscriptionProgressData = {
          showFromDate: subscriptionProgressData?.showFromDate || progress.show_from_date,
          status: progress.status,
          email: progress.email,
          emailMasked: progress.email_masked,
          voucherCode: progress.voucher_code,
          voucherExpiryDate,
        };

      }

      subscriptionProgressData.newsletterConsent = res?.newsletter_consent || false;
      saveLocalSubscriptionProgress(subscriptionProgressData);

      return res;

    } catch (error) {

      Logger.error(`cia|newsletterSubscriptionProgress error: ${error}`);

    }

    return null;

  }

  async function getSubscriptionProgress() {

    if (import.meta.server) {

      return null;

    }
    await fetchBloomreachSubscriptionProgress();

    return getLocalSubscriptionProgress();

  }

  return {
    voucherSettings,
    voucherValue,
    minimumPurchaseValue,
    fetchBloomreachSubscriptionProgress,
    getLocalSubscriptionProgress,
    saveLocalSubscriptionProgress,
    postponeWidgetDisplay,
    canRenderSubscriptionWidget,
    getSubscriptionProgress,
  };

};
